import React, { useEffect, useState } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { UserService } from '../../Service/UserService';
import {
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { useForm, useFieldArray } from 'react-hook-form';
import { Plus, Trash } from 'phosphor-react';
import { CashFlowService } from '../../Service/CashFlowService';
import { CashFlowChart } from '../../Utilis/Types/CashFlow';
import { useAppDispatch } from '../../store/hooks';
import { createNotification } from '../../store/notifications/notification-slice';
import ShareIcon from '../../assets/Icons/share-icon';
import { AuthService } from '../../Service/AuthService';
import { LocalStorageService } from '../../Service/LocalStorageService';
import { setToken, setClient } from '../../store/user/user-slice';

interface Column {
  id: 'year' | 'base-total-assets' | 'projected-total-assets';
  label: string;
  minWidth?: number;
  align?: 'right';
  format?: (value: number) => string;
}
const formatNumber = (value: number | string) => {
  const removeLetters = value?.toString().replace(/[a-zA-Z]/g, '');

  return removeLetters
    ?.toString()
    ?.replace(/,/g, '')
    ?.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

const formatNumberToFloat = (value: number | string) => {
  const removeLetters = value?.toString().replace(/[a-zA-Z]/g, '');

  return removeLetters
    ?.toString()
    ?.replace(/,/g, '')
    ?.replace(/\B(?=(\d{3})+(?!\d))/g, '');
};

const UserData = () => {
  const [clientDetails] = useOutletContext<any>();
  const [hoveredRow, setHoveredRow] = useState<number | null>(null);
  const [userName, setUserName] = useState<{ name: string; lastname: string }>({
    name: '',
    lastname: '',
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
    watch,
    setValue,
  } = useForm<any>();

  const { remove, append, fields } = useFieldArray({
    control,
    name: 'cashFlowChartRows',
  });

  const watchCashFlow = watch('cashFlowChartRows');

  const controlledFields = fields.map((item, i) => {
    return {
      ...item,
      ...watchCashFlow[i],
    };
  });

  const { userId } = useParams();
  const appDispatch = useAppDispatch();
  const navigate = useNavigate();

  const navigateToClient = async () => {
    const response = await AuthService.accessClientView({ clientId: userId! });
    LocalStorageService.setItem('accessToken', response.data.accessToken);

    appDispatch(setToken(response.data.accessToken));
    appDispatch(setClient(response.data));

    navigate('/home');
  };

  const updateDetails = async (values: any) => {
    try {
      await UserService.updateOne(userId!, {
        business_tax_strategies: +values.business_tax_strategies,
        financial_tax_strategies: +values.financial_tax_strategies,
        estimated_tax_savings: +formatNumberToFloat(
          values.estimated_tax_savings
        ),
        lifetime_impact_value: +formatNumberToFloat(
          values.lifetime_impact_value
        ),
        personal_tax_strategies: +values.personal_tax_strategies,
      });

      await CashFlowService.createOne({
        userId: userId!,
        items: values.cashFlowChartRows.map(
          (cashflow: {
            year: string | number;
            base_total_portfolio_assets: string | number;
            projected_total_portfolio_assets: string | number;
          }) => ({
            year: +cashflow.year,
            base_total_portfolio_assets: +formatNumberToFloat(
              cashflow?.base_total_portfolio_assets
            ),
            projected_total_portfolio_assets: +formatNumberToFloat(
              cashflow?.projected_total_portfolio_assets
            ),
          })
        ),
      });

      appDispatch(
        createNotification({
          type: 'success',
          message: 'User Updated Succesfuly!',
        })
      );
    } catch (error) {
      appDispatch(
        createNotification({
          type: 'error',
          message: 'Failed to update user , please try again later!',
        })
      );
    }
  };

  useEffect(() => {
    if (!!clientDetails) {
      const fetchUser = async () => {
        setUserName({
          name: clientDetails.name,
          lastname: clientDetails.lastName,
        });
        reset({
          business_tax_strategies: clientDetails.business_tax_strategies,
          financial_tax_strategies: clientDetails.financial_tax_strategies,
          estimated_tax_savings: formatNumber(
            clientDetails.estimated_tax_savings
          ),
          lifetime_impact_value: formatNumber(
            clientDetails.lifetime_impact_value
          ),
          personal_tax_strategies: clientDetails.personal_tax_strategies,
          cashFlowChartRows:
            clientDetails.cashFlow.map((cashflow: CashFlowChart) => ({
              year: cashflow.year,
              base_total_portfolio_assets: formatNumber(
                cashflow?.base_total_portfolio_assets
              ),
              projected_total_portfolio_assets: formatNumber(
                cashflow?.projected_total_portfolio_assets
              ),
            })) || [],
        });
      };
      fetchUser();
    }
  }, []);

  const strategyDetails = [
    {
      fieldTitle: 'Personal Tax Strategies',
      fieldInput: 'personal_tax_strategies',
    },
    {
      fieldTitle: 'Business Tax Strategies',
      fieldInput: 'business_tax_strategies',
    },
    {
      fieldTitle: 'Financial Tax Strategies',
      fieldInput: 'financial_tax_strategies',
    },
  ];

  const valueDetails = [
    {
      fieldTitle: 'Estimated Tax Savings',
      fieldInput: 'estimated_tax_savings',
    },
    {
      fieldTitle: 'Lifetime Impact Value',
      fieldInput: 'lifetime_impact_value',
    },
  ];

  const columns: Column[] = [
    { id: 'year', label: 'Year', minWidth: 170 },
    {
      id: 'base-total-assets',
      label: 'Base Total Portfolio Assets',
      minWidth: 100,
    },
    {
      id: 'projected-total-assets',
      label: 'Projected Total Portfolio Assets',
      minWidth: 170,
      align: 'right',
      format: (value: number) => value.toLocaleString('en-US'),
    },
  ];

  return (
    <Box>
      <Box padding={'40px 72px'}>
        <Typography
          fontSize={'24px'}
          lineHeight={'29px'}
          fontWeight={'600'}
          color={'#000'}
          mb={'40px'}
        >
          {userName?.name} {userName.lastname}
          <Button
            style={{
              marginLeft: '36px',
              background: 'var(--Primary-Color, #3293DB)',
              border: '1px solid var(--Primary-Color, #3293DB)',
              borderRadius: '4px',
              boxShadow: '0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
              fontFamily: 'Inter',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: 600,
              lineHeight: '20px',
              textTransform: 'none',
              color: 'var(--TextColor, #FDFDFD)',
              gap: '8px',
            }}
            onClick={navigateToClient}
          >
            Client View
            <ShareIcon color='#FDFDFD' />
          </Button>
        </Typography>
        <form onSubmit={handleSubmit(updateDetails)} action='submit'>
          <Box>
            <Typography
              fontSize={'20px'}
              lineHeight={'24px'}
              fontWeight={'500'}
            >
              Strategy Details
            </Typography>
            <Box
              sx={{
                display: 'flex',
                gap: '86px',
                marginTop: '28px',
                marginBottom: '4px',
              }}
            >
              {strategyDetails.map((detail: any, index) => (
                <Box key={index}>
                  <Typography
                    fontSize={'14px'}
                    lineHeight={'21px'}
                    fontWeight={'600'}
                    mb={'7px'}
                  >
                    {detail.fieldTitle}
                  </Typography>
                  <TextField
                    placeholder='Enter a number'
                    {...register(detail?.fieldInput)}
                    InputProps={{
                      sx: {
                        borderRadius: '16px',
                        padding: 0,
                      },
                    }}
                    inputProps={{
                      style: {
                        padding: '12px 14px',
                      },
                    }}
                    style={{
                      maxWidth: '300px',
                      width: '100%',
                    }}
                  />
                </Box>
              ))}
            </Box>
          </Box>
          <Box marginTop={'50px'}>
            <Typography
              fontSize={'20px'}
              lineHeight={'24px'}
              fontWeight={'500'}
            >
              Value Details
            </Typography>
            <Box
              sx={{
                display: 'flex',
                gap: '86px',
                marginTop: '28px',
                marginBottom: '4px',
              }}
            >
              {valueDetails.map((detail: any, index) => (
                <Box key={index}>
                  <Typography
                    fontSize={'14px'}
                    lineHeight={'21px'}
                    fontWeight={'600'}
                    mb={'7px'}
                  >
                    {detail.fieldTitle}
                  </Typography>
                  <TextField
                    placeholder='Enter $ value'
                    {...register(detail?.fieldInput)}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const value = formatNumberToFloat(e.target.value);
                      setValue(detail?.fieldInput, formatNumber(value));
                    }}
                    InputProps={{
                      sx: {
                        borderRadius: '16px',
                      },
                      startAdornment: (
                        <InputAdornment position='start'>$</InputAdornment>
                      ),
                    }}
                    inputProps={{
                      style: {
                        padding: '12px 14px',
                      },
                    }}
                    sx={{
                      maxWidth: '300px',
                      width: '100%',
                    }}
                  />
                </Box>
              ))}
            </Box>
          </Box>
          <Box marginTop={'50px'}>
            <Typography
              fontSize={'20px'}
              lineHeight={'24px'}
              fontWeight={'500'}
            >
              Cash Flow Chart Details
            </Typography>

            <Box
              sx={{
                marginTop: '28px',
                marginBottom: '7px',
                display: 'grid',
                gridTemplateColumns: '30% 30% 30%',
                gridGap: '50px',
              }}
            >
              {columns.map(column => (
                <Typography
                  key={column.label}
                  fontSize={'14px'}
                  lineHeight={'21px'}
                  fontWeight={'600'}
                >
                  {column.label}
                </Typography>
              ))}
            </Box>
            <Box
              sx={{
                marginBottom: '4px',
              }}
            >
              {controlledFields?.map((item, i: number) => (
                <Box
                  key={item.id}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '30% 30% 33%',
                    gridGap: '50px',
                    marginBottom: '16px',
                    background: hoveredRow === i ? '#E5FAFF' : 'transparent',
                    padding: '8px',
                  }}
                  onMouseEnter={() => setHoveredRow(i)}
                  onMouseLeave={() => setHoveredRow(null)}
                >
                  <TextField
                    placeholder='Enter Number'
                    {...register(`cashFlowChartRows.${i}.year` as const)}
                    variant='outlined'
                    type='number'
                    InputProps={{
                      sx: {
                        borderRadius: '16px',
                        background: '#fff',
                      },
                    }}
                    sx={{
                      maxWidth: '200px',
                      width: '100%',
                    }}
                    inputProps={{
                      style: {
                        padding: '12px 14px',
                      },
                    }}
                  />
                  <TextField
                    placeholder='Enter Number'
                    {...register(
                      `cashFlowChartRows.${i}.base_total_portfolio_assets` as const
                    )}
                    onChange={e => {
                      const value = formatNumberToFloat(e.target.value);
                      setValue(
                        `cashFlowChartRows.${i}.base_total_portfolio_assets` as const,
                        formatNumber(value)
                      );
                    }}
                    variant='outlined'
                    InputProps={{
                      sx: {
                        borderRadius: '16px',
                        background: '#fff',
                      },
                      startAdornment: (
                        <InputAdornment position='start'>$</InputAdornment>
                      ),
                    }}
                    sx={{
                      maxWidth: '250px',
                      width: '100%',
                    }}
                    inputProps={{
                      style: {
                        padding: '12px 14px',
                      },
                    }}
                  />
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <TextField
                      placeholder='Enter Number'
                      {...register(
                        `cashFlowChartRows.${i}.projected_total_portfolio_assets` as const
                      )}
                      onChange={e => {
                        const value = formatNumberToFloat(e.target.value);
                        setValue(
                          `cashFlowChartRows.${i}.projected_total_portfolio_assets` as const,
                          formatNumber(value)
                        );
                      }}
                      variant='outlined'
                      InputProps={{
                        sx: {
                          borderRadius: '16px',
                          background: '#fff',
                        },
                        startAdornment: (
                          <InputAdornment position='start'>$</InputAdornment>
                        ),
                      }}
                      sx={{
                        maxWidth: '250px',
                        width: '100%',
                      }}
                      inputProps={{
                        style: {
                          padding: '12px 14px',
                        },
                      }}
                    />
                    {hoveredRow === i && (
                      <Trash
                        size={'24px'}
                        color='#655C68'
                        cursor={'pointer'}
                        style={{ marginLeft: '34px' }}
                        onClick={() => {
                          remove(i);
                          setHoveredRow(null);
                        }}
                      />
                    )}
                  </Box>
                </Box>
              ))}
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                marginTop: '20px',
                gap: '11px',
                cursor: 'pointer',
              }}
            >
              <Plus size={20} color={'#a6a6a6'} />
              <Typography
                fontSize={'14px'}
                lineHeight={'17px'}
                fontWeight={'500'}
                color={'#a6a6a6'}
                onClick={() => {
                  append({
                    year: '',
                    base_total_portfolio_assets: '',
                    projected_total_portfolio_assets: '',
                  });
                }}
              >
                Add row
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              marginTop: '72px',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '24px',
            }}
          >
            <Button
              sx={{
                maxWidth: '126px',
                width: '100%',
                fontSize: '14px',
                color: '#655C68',
                lineHeight: '17px',
                borderColor: '#E3DDE6',
              }}
              variant='outlined'
            >
              Cancel
            </Button>
            <Button
              sx={{
                background: '#3293DB',
                color: '#fff',
                maxWidth: '126px',
                width: '100%',
                fontSize: '14px',
                lineHeight: '17px',
              }}
              variant='contained'
              type='submit'
            >
              Update
            </Button>
          </Box>
        </form>
      </Box>
    </Box>
  );
};

export default UserData;
